import React from "react";

import { AgGridReactProps } from "@ag-grid-community/react/lib/agGridReact";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { AnyEntity } from "@mapmycustomers/shared/types/entity";

import ActivityAnnotationLinkCellRenderer from "@app/component/grid/gridComponents/cellRenderers/ActivityAnnotationLinkCellRenderer";
import ActivityTypeCellRenderer from "@app/component/grid/gridComponents/cellRenderers/ActivityTypeCellRenderer";
import ColorCellRenderer from "@app/component/grid/gridComponents/cellRenderers/ColorCellRenderer";
import CountryCellRenderer from "@app/component/grid/gridComponents/cellRenderers/CountryCellRenderer";
import CrmLinkCellRenderer from "@app/component/grid/gridComponents/cellRenderers/CrmLinkCellRenderer";
import EmailCellRenderer from "@app/component/grid/gridComponents/cellRenderers/EmailCellRenderer";
import FrequencyCellRenderer from "@app/component/grid/gridComponents/cellRenderers/FrequencyCellRenderer";
import FrequencyDaysLeftCellRenderer from "@app/component/grid/gridComponents/cellRenderers/FrequencyDaysLeftCellRenderer";
import FrequencyIntervalCellRenderer from "@app/component/grid/gridComponents/cellRenderers/FrequencyIntervalCellRenderer";
import GroupsCellRenderer from "@app/component/grid/gridComponents/cellRenderers/GroupsCellRenderer";
import LocationCellRenderer from "@app/component/grid/gridComponents/cellRenderers/LocationCellRenderer";
import MultiOptionCustomFieldCellRenderer from "@app/component/grid/gridComponents/cellRenderers/MultiOptionCustomFieldCellRenderer";
import NameCellRenderer from "@app/component/grid/gridComponents/cellRenderers/NameCellRenderer";
import NoteCellRenderer from "@app/component/grid/gridComponents/cellRenderers/NoteCellRenderer";
import NotesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/NotesCellRenderer";
import PhoneCellRenderer from "@app/component/grid/gridComponents/cellRenderers/PhoneCellRenderer";
import PrimaryAssociationCellRenderer from "@app/component/grid/gridComponents/cellRenderers/PrimaryAssociationCellRenderer";
import RelationshipCellRenderer from "@app/component/grid/gridComponents/cellRenderers/RelationshipCellRenderer";
import ReliabilityCellRenderer from "@app/component/grid/gridComponents/cellRenderers/ReliabilityCellRenderer";
import RoutesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/RoutesCellRenderer";
import StageCellRenderer from "@app/component/grid/gridComponents/cellRenderers/StageCellRenderer";
import TeamsCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TeamsCellRenderer";
import TerritoriesCellRenderer from "@app/component/grid/gridComponents/cellRenderers/TerritoriesCellRenderer";
import UserCellRenderer from "@app/component/grid/gridComponents/cellRenderers/UserCellRenderer";
import WebsiteCellRenderer from "@app/component/grid/gridComponents/cellRenderers/WebsiteCellRenderer";
import columnTypes from "@app/component/grid/util/column-types/server-side-grid-column-types";
import AgGridListViewServerSideGrid from "@app/component/view/ListView/AgGridListViewServerSideGrid";
import useDrillDownView from "@app/scene/dashboard/components/cards/common/DrillDownModal/util/useDrillDownView";

import styles from "./Grid.module.scss";

const previewColumnTypes = { ...columnTypes, [FieldFeature.WITH_CHECKBOX_SELECTION]: {} };

interface Props {
  context?: AgGridReactProps["context"];
  frameworkComponents?: AgGridReactProps["frameworkComponents"];
  loading?: boolean;
  onActivityTypeView?: (entity: AnyEntity, element: HTMLElement) => void;
  onRecordView?: (entity: AnyEntity) => void;
  totalFilteredRecords: number;
}

const Grid: React.FC<Props> = ({
  context,
  frameworkComponents,
  loading,
  onActivityTypeView,
  onRecordView,
  totalFilteredRecords,
}) => {
  const {
    viewState: { range },
  } = useDrillDownView();

  return (
    <section className={styles.gridContainer}>
      <AgGridListViewServerSideGrid
        columnTypes={previewColumnTypes}
        context={{
          ...context,
          onActivityTypeView,
          onRelationshipView: onRecordView,
          onView: onRecordView,
        }}
        enableCellTextSelection // Note: When this is set to true, the clipboard service is disabled.
        frameworkComponents={{
          activityAnnotationLinkCellRenderer: ActivityAnnotationLinkCellRenderer,
          activityTypeCellRenderer: ActivityTypeCellRenderer,
          colorCellRenderer: ColorCellRenderer,
          countryCellRenderer: CountryCellRenderer,
          crmLinkCellRenderer: CrmLinkCellRenderer,
          emailCellRenderer: EmailCellRenderer,
          frequencyCellRenderer: FrequencyCellRenderer,
          frequencyDaysLeftCellRenderer: FrequencyDaysLeftCellRenderer,
          frequencyIntervalCellRenderer: FrequencyIntervalCellRenderer,
          groupsCellRenderer: GroupsCellRenderer,
          locationCellRenderer: LocationCellRenderer,
          multiOptionCustomFieldCellRenderer: MultiOptionCustomFieldCellRenderer,
          nameCellRenderer: NameCellRenderer,
          noteCellRenderer: NoteCellRenderer,
          notesCellRenderer: NotesCellRenderer,
          phoneCellRenderer: PhoneCellRenderer,
          primaryAssociationCellRenderer: PrimaryAssociationCellRenderer,
          relationshipCellRenderer: RelationshipCellRenderer,
          reliabilityCellRenderer: ReliabilityCellRenderer,
          routesCellRenderer: RoutesCellRenderer,
          stageCellRenderer: StageCellRenderer,
          teamsCellRenderer: TeamsCellRenderer,
          territoriesCellRenderer: TerritoriesCellRenderer,
          userCellRenderer: UserCellRenderer,
          websiteCellRenderer: WebsiteCellRenderer,
          ...frameworkComponents,
        }}
        paginationPageSize={range.endRow - range.startRow}
        showCreateCta={false}
        showNoRows={!totalFilteredRecords && !loading}
        suppressPaginationPanel
      />
    </section>
  );
};

export default Grid;
